import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Compressor from "compressorjs";

import { i18n } from "../../translate/i18n";

import api, { getMediaUrl } from "../../services/api";
import toastError from "../../errors/toastError";
import { Checkbox, Stack } from "@mui/material";

const useStyles = makeStyles(theme => ({
  ...getStandardButtonStyles(theme),
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },

  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },

  btnWrapper: {
    position: "relative"
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const FlowBuilderAddVideoModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);

  const [loading, setLoading] = useState(false);

  const [record, setRecord] = useState(false);

  const [preview, setPreview] = useState();

  const [labels, setLabels] = useState({
    title: i18n.t("flowBuilder.video.addTitle") || "Adicionar video ao fluxo",
    btn: i18n.t("common.add") || "Adicionar"
  });

  const [name, setName] = useState("");
  const [caption, setCaption] = useState("");
  const [medias, setMedias] = useState([]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: i18n.t("flowBuilder.video.editTitle") || "Editar video",
        btn: i18n.t("common.save")
      });
      const companyId = localStorage.getItem("companyId") || 1;
      // [ANTIGRAVITY] Fix: Use centralized media URL helper
      setPreview(getMediaUrl(data.data.url, companyId));
      setRecord(data.data.record)
      setName(data.data.name || "");
      setCaption(data.data.caption || "");
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: i18n.t("flowBuilder.video.addTitle") || "Adicionar video ao fluxo",
        btn: i18n.t("common.add") || "Adicionar"
      });
      setName("");
      setCaption("");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = async () => {
    if (open === "edit" && medias.length === 0) {
      handleClose();
      onUpdate({
        ...data,
        data: {
          ...data.data,
          name: name,
          caption: caption,
          url: data.data.url ? data.data.url.split(/[/\\]/).pop() : "",
          record: record
        }
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("fromMe", true);
    formData.append("typeArch", "flow");

    medias.forEach(async (media, idx) => {
      const file = media;

      if (!file) {
        return;
      }

      // Video logic implies we just append, no compressor for video here (unlike image modal)
      // The original code had compressor logic inside video modal but checked for media.type "image"
      // If we are strictly uploading videos (accept="video/mp4"), we might not need compressor, 
      // but keeping original logic structure for safety.

      if (media?.type.split("/")[0] == "image") {
        // ... existing compressor logic ...
        new Compressor(file, {
          quality: 0.7,
          async success(media) {
            formData.append("medias", media);
            formData.append("body", media.name);
          },
          error(err) {
            toast.error(i18n.t("flowBuilder.video.error") || "Erro ao enviar video");
            console.log(err.message);
          }
        });
      } else {
        formData.append("medias", media);
        formData.append("body", media.name);
      }
    });

    try {
      // NOTE: Using 'audio' endpoint as per original code, assuming backend handles video there too or mixed types
      const { data: resData } = await api.post("/flowbuilder/audio", formData);
      handleClose();

      if (open === "edit") {
        onUpdate({
          ...data,
          data: {
            ...data.data,
            name: name,
            caption: caption,
            url: resData.name ? resData.name.split(/[/\\]/).pop() : "",
            record: record
          }
        });
      } else {
        onSave({
          url: resData.name ? resData.name.split(/[/\\]/).pop() : "",
          name: name,
          caption: caption
        });
      }

      toast.success(i18n.t("flowBuilder.video.success") || "Video adicionado com sucesso!");
      setMedias([]);
      setPreview();
      setName("");
      setCaption("");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    if (e.target.files[0].size > 20000000) {
      toast.error(i18n.t("flowBuilder.video.tooLarge") || "Arquivo é muito grande! 20MB máximo")
      return
    }

    const selectedMedias = Array.from(e.target.files);
    setPreview((URL.createObjectURL(e.target.files[0])));
    setMedias(selectedMedias);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Stack gap={"16px"}>
              <TextField
                label={i18n.t("flowBuilder.form.name") || "Nome"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
              />
              <TextField
                label="Texto/Caption (opcional)"
                placeholder="Agrega un texto que se enviará junto con el video"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={4}
                helperText="El texto se enviará como caption del video (como en WhatsApp)"
              />
              {preview && (
                <Stack direction={'row'} justifyContent={'center'} sx={{ bgcolor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                  <video
                    key={preview}
                    controls="controls"
                    style={{ width: "100%", maxHeight: "350px", objectFit: "contain" }}
                    src={preview}
                  >
                    {i18n.t("flowBuilder.video.browserSupport") || "Seu navegador não suporta HTML5"}
                  </video>
                </Stack>
              )}
              {!loading && (
                <>
                  <Button variant="contained" component="label">
                    {i18n.t("flowBuilder.video.upload") || "Enviar video"}
                    <input
                      type="file"
                      accept="video/mp4"
                      disabled={loading}
                      hidden
                      onChange={handleChangeMedias}
                    />
                  </Button>
                  <Typography>{i18n.t("flowBuilder.video.attention") || "ATENÇÃO! Apenas videos em MP4!"}</Typography>
                </>
              )}
              {loading && (
                <>
                  <Stack justifyContent={"center"} alignSelf={"center"}>
                    <CircularProgress />
                  </Stack>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            {!loading && (
              <>
                <Button onClick={() => {
                    handleClose();
                    setMedias([]);
                    setPreview();
                    setCaption("");
                  }} 
                  className={classes.secondaryButton}
                >
                  {i18n.t("common.cancel") || "Cancelar"}
                </Button>
                <Button 
                  type="submit" 
                  onClick={() => handleSaveContact()} 
                  disabled={loading} 
                  className={classes.applyButton}
                >
                  {`${labels.btn}`}
                </Button>
              </>
            )}
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddVideoModal;
